import React, { useEffect, useState, useRef } from "react";
import {
  Modal,
  Button,
  Spinner,
  Form,
  InputGroup,
  Dropdown,
} from "react-bootstrap";
import apiClient from "../../../api/apiClient";
import { useTranslation } from "react-i18next";
import {
  FaRobot,
  FaUser,
  FaTimes,
  FaSearch,
  FaFilter,
  FaCalendarAlt,
  FaSync,
  FaChevronDown,
} from "react-icons/fa";
import { Icon } from "../../../components/common/Icon";

interface Message {
  id: number;
  conversation_id: number;
  ai_chatbot_id: number;
  role: string;
  content: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface AIChatbotConversationModalProps {
  show: boolean;
  onHide: () => void;
  conversationId: number | null;
  sessionId: string;
  chatbotId: number;
  chatbotName: string;
}

const AIChatbotConversationModal: React.FC<AIChatbotConversationModalProps> = ({
  show,
  onHide,
  conversationId,
  sessionId,
  chatbotId,
  chatbotName,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const apiUrl =  process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
  useEffect(() => {
    if (show && conversationId) {
      fetchMessages();
    }
  }, [show, conversationId]);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    filterMessages();
  }, [messages, searchTerm, dateFilter, customStartDate, customEndDate]);

  const fetchMessages = async () => {
    if (!conversationId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/ai-chatbots/${conversationId}/messages`, { params: { skip: 0, limit: 100 } });

      const raw = (response as any).data || response;
      const sortedMessages = Array.isArray(raw)
        ? raw.sort(
            (a: Message, b: Message) =>
              new Date(a.created_at).getTime() -
              new Date(b.created_at).getTime()
          )
        : [];

      setMessages(sortedMessages);
    } catch (err) {
      console.error("Error fetching messages:", err);
      setError("Failed to load conversation messages. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const filterMessages = () => {
    let filtered = [...messages];

    // Text search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter((message) =>
        message.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Date filter
    if (dateFilter !== "all") {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const lastWeek = new Date(today);
      lastWeek.setDate(lastWeek.getDate() - 7);
      const lastMonth = new Date(today);
      lastMonth.setMonth(lastMonth.getMonth() - 1);

      filtered = filtered.filter((message) => {
        const messageDate = new Date(message.created_at);

        switch (dateFilter) {
          case "today":
            return messageDate >= today;
          case "yesterday":
            return messageDate >= yesterday && messageDate < today;
          case "lastWeek":
            return messageDate >= lastWeek;
          case "lastMonth":
            return messageDate >= lastMonth;
          case "custom":
            if (customStartDate && customEndDate) {
              const startDate = new Date(customStartDate);
              const endDate = new Date(customEndDate);
              endDate.setHours(23, 59, 59, 999); // Include the entire end date
              return messageDate >= startDate && messageDate <= endDate;
            }
            return true;
          default:
            return true;
        }
      });
    }

    setFilteredMessages(filtered);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setDateFilter("all");
    setCustomStartDate("");
    setCustomEndDate("");
    setShowCustomDatePicker(false);
  };

  const handleDateFilterChange = (filter: string) => {
    setDateFilter(filter);
    if (filter === "custom") {
      setShowCustomDatePicker(true);
    } else {
      setShowCustomDatePicker(false);
      setCustomStartDate("");
      setCustomEndDate("");
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString();
    }
  };

  const shouldShowDateSeparator = (
    currentMessage: Message,
    previousMessage: Message | null
  ) => {
    if (!previousMessage) return true;

    const currentDate = new Date(currentMessage.created_at).toDateString();
    const previousDate = new Date(previousMessage.created_at).toDateString();

    return currentDate !== previousDate;
  };

  const highlightSearchTerm = (text: string) => {
    if (!searchTerm.trim()) return text;

    const regex = new RegExp(`(${searchTerm})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="search-highlight">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const getDateFilterLabel = () => {
    switch (dateFilter) {
      case "all":
        return "All Time";
      case "today":
        return "Today";
      case "yesterday":
        return "Yesterday";
      case "lastWeek":
        return "Last Week";
      case "lastMonth":
        return "Last Month";
      case "custom":
        if (customStartDate && customEndDate) {
          return `${new Date(customStartDate).toLocaleDateString()} - ${new Date(customEndDate).toLocaleDateString()}`;
        }
        return "Custom Range";
      default:
        return "All Time";
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        centered
        className="premium-conversation-modal"
        backdrop="static"
      >
        <div className="modal-wrapper">
          {/* Premium Header */}
          <div className="premium-header">
            <div className="header-left">
              <div className="chatbot-avatar-premium">
                <div className="avatar-inner">
                  <Icon icon={FaRobot} />
                </div>
                <div className="status-indicator-premium"></div>
              </div>
              <div className="chatbot-info">
                <h6 className="chatbot-title">#{chatbotName}</h6>
                <div className="session-details">
                  <span className="session-badge">{sessionId}</span>
                </div>
              </div>
            </div>
            <div className="header-actions">
              <button
                className="action-btn-premium refresh-btn"
                onClick={fetchMessages}
                disabled={loading}
                title="Refresh messages"
              >
                <Icon icon={FaSync} />
              </button>
              <button
                className={`action-btn-premium filter-btn ${showFilters ? "active" : ""}`}
                onClick={() => setShowFilters(!showFilters)}
                title="Toggle filters"
              >
                <Icon icon={FaFilter} />
              </button>
              <button className="action-btn-premium close-btn" onClick={onHide}>
                <Icon icon={FaTimes} />
              </button>
            </div>
          </div>

          {/* Premium Filter Bar */}
          {showFilters && (
            <div className="premium-filter-bar">
              <div className="filter-row">
                <div className="search-section-premium">
                  <div className="search-input-wrapper">
                    <Icon icon={FaSearch} className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search in conversation..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input-premium"
                    />
                    {searchTerm && (
                      <button
                        className="clear-search-btn"
                        onClick={() => setSearchTerm("")}
                      >
                        <Icon icon={FaTimes} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="date-filter-section-premium">
                  <Dropdown>
                    <Dropdown.Toggle as="div" className="date-filter-toggle">
                      <Icon icon={FaCalendarAlt} />
                      <span>{getDateFilterLabel()}</span>
                      <Icon icon={FaChevronDown} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="date-filter-menu">
                      <Dropdown.Item
                        onClick={() => handleDateFilterChange("all")}
                      >
                        All Time
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleDateFilterChange("today")}
                      >
                        Today
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleDateFilterChange("yesterday")}
                      >
                        Yesterday
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleDateFilterChange("lastWeek")}
                      >
                        Last Week
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleDateFilterChange("lastMonth")}
                      >
                        Last Month
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item
                        onClick={() => handleDateFilterChange("custom")}
                      >
                        Custom Range
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                {(searchTerm || dateFilter !== "all") && (
                  <button
                    className="clear-filters-btn-premium"
                    onClick={clearFilters}
                  >
                    Clear All
                  </button>
                )}
              </div>

              {/* Custom Date Range Picker */}
              {showCustomDatePicker && (
                <div className="custom-date-picker">
                  <div className="date-inputs">
                    <div className="date-input-group">
                      <label>From</label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="date-input-premium"
                      />
                    </div>
                    <div className="date-separator-line"></div>
                    <div className="date-input-group">
                      <label>To</label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="date-input-premium"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Premium Messages Area */}
          <div className="premium-messages-area">
            {loading ? (
              <div className="state-container-premium loading-state">
                <div className="state-content-premium">
                  <div className="loading-animation">
                    <div className="loading-dots">
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                  </div>
                  <h6>Loading Messages</h6>
                  <p>Fetching your conversation history...</p>
                </div>
              </div>
            ) : error ? (
              <div className="state-container-premium error-state">
                <div className="state-content-premium">
                  <div className="error-icon-premium">⚠️</div>
                  <h6>Connection Error</h6>
                  <p>{error}</p>
                  <button className="retry-btn-premium" onClick={fetchMessages}>
                    <Icon icon={FaSync} /> Retry
                  </button>
                </div>
              </div>
            ) : filteredMessages.length === 0 ? (
              <div className="state-container-premium empty-state">
                <div className="state-content-premium">
                  <div className="empty-icon-premium">
                    {searchTerm || dateFilter !== "all" ? "🔍" : "💬"}
                  </div>
                  <h6>
                    {searchTerm || dateFilter !== "all"
                      ? "No Results Found"
                      : "No Messages Yet"}
                  </h6>
                  <p>
                    {searchTerm || dateFilter !== "all"
                      ? "Try adjusting your search or filter criteria."
                      : "Start a conversation to see messages here."}
                  </p>
                  {(searchTerm || dateFilter !== "all") && (
                    <button
                      className="clear-filters-btn-premium"
                      onClick={clearFilters}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="messages-list-premium">
                {filteredMessages.map((message, index) => {
                  const previousMessage =
                    index > 0 ? filteredMessages[index - 1] : null;
                  const showDateSeparator = shouldShowDateSeparator(
                    message,
                    previousMessage
                  );

                  return (
                    <React.Fragment key={message.id}>
                      {showDateSeparator && (
                        <div className="date-separator-premium">
                          <span className="date-text-premium">
                            {formatDate(message.created_at)}
                          </span>
                        </div>
                      )}

                      <div
                        className={`message-row-premium ${message.role === "user" ? "user-row" : "bot-row"}`}
                      >
                        <div className="message-wrapper-premium">
                          <div className="message-bubble-premium">
                            <div className="message-content-premium">
                              {highlightSearchTerm(message.content)}
                            </div>
                            <div className="message-meta-premium">
                              <span className="message-time-premium">
                                {formatTime(message.created_at)}
                              </span>
                              {message.status === "failed" && (
                                <span className="message-status-failed">
                                  Failed
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Premium Footer */}
          <div className="premium-footer">
            <div className="footer-info-premium">
              <div className="message-count-premium">
                <span className="count-number">
                  {filteredMessages.length !== messages.length
                    ? `${filteredMessages.length} of ${messages.length}`
                    : `${messages.length}`}
                </span>
                <span className="count-label">messages</span>
              </div>
              {(searchTerm || dateFilter !== "all") && (
                <div className="filter-indicator-premium">
                  <Icon icon={FaFilter} />
                  Filtered
                </div>
              )}
            </div>
            <button className="close-modal-btn-premium" onClick={onHide}>
              Close
            </button>
          </div>
        </div>
      </Modal>

      <style>{`
        .premium-conversation-modal {
          --primary-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          --secondary-gradient: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
          --success-gradient: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
          --danger-gradient: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
          --dark-gradient: linear-gradient(135deg, #434343 0%, #000000 100%);
          
          --primary-purple: #667eea;
          --primary-purple-light: #8b9cf7;
          --primary-purple-dark: #5a6fd8;
          --accent-pink: #f093fb;
          --accent-blue: #4facfe;
          --success-green: #00f2fe;
          --danger-red: #fa709a;
          --warning-orange: #fee140;
          
          --neutral-50: #fafafa;
          --neutral-100: #f5f5f5;
          --neutral-200: #e5e5e5;
          --neutral-300: #d4d4d4;
          --neutral-400: #a3a3a3;
          --neutral-500: #737373;
          --neutral-600: #525252;
          --neutral-700: #404040;
          --neutral-800: #262626;
          --neutral-900: #171717;
          
          --glass-bg: rgba(255, 255, 255, 0.95);
          --glass-border: rgba(255, 255, 255, 0.2);
          --backdrop-blur: blur(20px);
          
          --shadow-xs: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          --shadow-sm: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
          --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
          --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
          --shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
          --shadow-2xl: 0 25px 50px -12px rgb(0 0 0 / 0.25);
          
          --border-radius-sm: 6px;
          --border-radius: 12px;
          --border-radius-lg: 20px;
          --border-radius-xl: 24px;
          --border-radius-full: 9999px;
          
          --transition-fast: all 0.15s ease;
          --transition-normal: all 0.3s ease;
          --transition-slow: all 0.5s ease;
        }

        .premium-conversation-modal .modal-dialog {
          max-width: 950px;
          margin: 1rem auto;
        }

        .premium-conversation-modal .modal-content {
          border: none;
          border-radius: var(--border-radius-xl);
          box-shadow: var(--shadow-2xl);
          overflow: hidden;
          height: 88vh;
          max-height: 800px;
          background: var(--glass-bg);
          backdrop-filter: var(--backdrop-blur);
        }

        .modal-wrapper {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: transparent;
        }

        /* Premium Header */
        .premium-header {
          background: var(--primary-gradient);
          color: white;
          padding: 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
          overflow: hidden;
        }

        .premium-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="75" cy="75" r="1" fill="rgba(255,255,255,0.05)"/><circle cx="50" cy="10" r="0.5" fill="rgba(255,255,255,0.08)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
          opacity: 0.3;
          pointer-events: none;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 1.25rem;
          position: relative;
          z-index: 1;
        }

        .chatbot-avatar-premium {
          position: relative;
          width: 60px;
          height: 60px;
        }

        .avatar-inner {
          width: 100%;
          height: 100%;
          background: rgba(255, 255, 255, 0.2);
          border-radius: var(--border-radius-full);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          backdrop-filter: var(--backdrop-blur);
          border: 2px solid rgba(255, 255, 255, 0.3);
          transition: var(--transition-normal);
        }

        .avatar-inner:hover {
          transform: scale(1.05);
          background: rgba(255, 255, 255, 0.3);
        }

        .status-indicator-premium {
          position: absolute;
          bottom: 4px;
          right: 4px;
          width: 16px;
          height: 16px;
          background: var(--success-gradient);
          border-radius: var(--border-radius-full);
          border: 3px solid white;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        .chatbot-info {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .chatbot-title {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .session-details {
          display: flex;
          align-items: center;
        }

        .session-badge {
          font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
          background: rgba(255, 255, 255, 0.2);
          padding: 0.375rem 0.75rem;
          border-radius: var(--border-radius-full);
          font-size: 0.8rem;
          font-weight: 600;
          backdrop-filter: var(--backdrop-blur);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          position: relative;
          z-index: 1;
        }

        .action-btn-premium {
          background: rgba(255, 255, 255, 0.15);
          border: none;
          border-radius: var(--border-radius-full);
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          cursor: pointer;
          transition: var(--transition-normal);
          backdrop-filter: var(--backdrop-blur);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .action-btn-premium:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }

        .action-btn-premium:active {
          transform: translateY(0);
        }

        .action-btn-premium:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .action-btn-premium.active {
          background: rgba(255, 255, 255, 0.3);
          box-shadow: var(--shadow-md);
        }

        .refresh-btn svg {
          animation: ${loading ? "spin 1s linear infinite" : "none"};
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Premium Filter Bar */
        .premium-filter-bar {
          background: var(--neutral-50);
          border-bottom: 1px solid var(--neutral-200);
          padding: 1.5rem;
        }

        .filter-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .search-section-premium {
          flex: 1;
          min-width: 250px;
        }

        .search-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          color: var(--neutral-400);
          z-index: 1;
        }

        .search-input-premium {
          width: 100%;
          padding: 0.875rem 1rem 0.875rem 2.75rem;
          border: 2px solid var(--neutral-200);
          border-radius: var(--border-radius-full);
          font-size: 0.95rem;
          background: white;
          transition: var(--transition-normal);
          outline: none;
        }

        .search-input-premium:focus {
          border-color: var(--primary-purple);
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .clear-search-btn {
          position: absolute;
          right: 0.75rem;
          background: var(--neutral-200);
          border: none;
          border-radius: var(--border-radius-full);
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: var(--transition-fast);
        }

        .clear-search-btn:hover {
          background: var(--neutral-300);
        }

        .date-filter-section-premium {
          flex-shrink: 0;
        }

        .date-filter-toggle {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1.25rem;
          background: white;
          border: 2px solid var(--neutral-200);
          border-radius: var(--border-radius-full);
          cursor: pointer;
          transition: var(--transition-normal);
          font-weight: 500;
          color: var(--neutral-700);
          min-width: 180px;
          justify-content: space-between;
        }

        .date-filter-toggle:hover {
          border-color: var(--primary-purple);
          box-shadow: var(--shadow-sm);
        }

        .date-filter-menu {
          border-radius: var(--border-radius);
          border: none;
          box-shadow: var(--shadow-xl);
          padding: 0.5rem;
        }

        .date-filter-menu .dropdown-item {
          border-radius: var(--border-radius-sm);
          padding: 0.75rem 1rem;
          transition: var(--transition-fast);
        }

        .date-filter-menu .dropdown-item:hover {
          background: var(--primary-purple);
          color: white;
        }

        .clear-filters-btn-premium {
          background: var(--danger-gradient);
          border: none;
          border-radius: var(--border-radius-full);
          padding: 0.875rem 1.5rem;
          color: white;
          font-weight: 600;
                    cursor: pointer;
          transition: var(--transition-normal);
          box-shadow: var(--shadow-sm);
        }

        .clear-filters-btn-premium:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        /* Custom Date Range Picker */
        .custom-date-picker {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid var(--neutral-200);
        }

        .date-inputs {
          display: flex;
          align-items: center;
          gap: 1rem;
          justify-content: center;
        }

        .date-input-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .date-input-group label {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--neutral-600);
        }

        .date-input-premium {
          padding: 0.75rem 1rem;
          border: 2px solid var(--neutral-200);
          border-radius: var(--border-radius);
          font-size: 0.9rem;
          background: white;
          transition: var(--transition-normal);
          outline: none;
          min-width: 150px;
        }

        .date-input-premium:focus {
          border-color: var(--primary-purple);
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .date-separator-line {
          width: 20px;
          height: 2px;
          background: var(--neutral-300);
          border-radius: 1px;
          margin-top: 1.5rem;
        }

        /* Premium Messages Area */
        .premium-messages-area {
          flex: 1;
          overflow-y: auto;
          background: linear-gradient(180deg, var(--neutral-50) 0%, var(--neutral-100) 100%);
          position: relative;
        }

        /* State Containers */
        .state-container-premium {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          padding: 3rem;
        }

        .state-content-premium {
          text-align: center;
          max-width: 450px;
        }

        /* Loading State */
        .loading-animation {
          margin-bottom: 2rem;
        }

        .loading-dots {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
        }

        .loading-dots div {
          width: 12px;
          height: 12px;
          background: var(--primary-gradient);
          border-radius: var(--border-radius-full);
          animation: loadingBounce 1.4s ease-in-out infinite both;
        }

        .loading-dots div:nth-child(1) { animation-delay: -0.32s; }
        .loading-dots div:nth-child(2) { animation-delay: -0.16s; }

        @keyframes loadingBounce {
          0%, 80%, 100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }

        .loading-state h6 {
          margin: 0 0 0.75rem;
          color: var(--primary-purple);
          font-weight: 700;
          font-size: 1.1rem;
        }

        .loading-state p {
          color: var(--neutral-500);
          margin: 0;
          font-size: 0.95rem;
        }

        /* Error State */
        .error-icon-premium {
          font-size: 4rem;
          margin-bottom: 1.5rem;
          filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
        }

        .error-state h6 {
          color: var(--danger-red);
          margin-bottom: 0.75rem;
          font-weight: 700;
          font-size: 1.1rem;
        }

        .error-state p {
          color: var(--neutral-500);
          margin-bottom: 2rem;
          font-size: 0.95rem;
          line-height: 1.5;
        }

        .retry-btn-premium {
          background: var(--danger-gradient);
          border: none;
          border-radius: var(--border-radius-full);
          padding: 0.875rem 2rem;
          color: white;
          font-weight: 600;
          cursor: pointer;
          transition: var(--transition-normal);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          box-shadow: var(--shadow-sm);
        }

        .retry-btn-premium:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        /* Empty State */
        .empty-icon-premium {
          font-size: 4rem;
          margin-bottom: 1.5rem;
          filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
        }

        .empty-state h6 {
          color: var(--neutral-700);
          margin-bottom: 0.75rem;
          font-weight: 700;
          font-size: 1.1rem;
        }

        .empty-state p {
          color: var(--neutral-500);
          margin-bottom: 1.5rem;
          font-size: 0.95rem;
          line-height: 1.5;
        }

        /* Messages List */
        .messages-list-premium {
          padding: 2rem;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        /* Date Separator */
        .date-separator-premium {
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 2rem 0;
          position: relative;
        }

        .date-separator-premium::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent 0%, var(--neutral-300) 20%, var(--neutral-300) 80%, transparent 100%);
        }

        .date-text-premium {
          background: white;
          padding: 0.625rem 1.25rem;
          border-radius: var(--border-radius-full);
          font-size: 0.85rem;
          color: var(--neutral-600);
          font-weight: 600;
          border: 2px solid var(--neutral-200);
          position: relative;
          z-index: 1;
          box-shadow: var(--shadow-xs);
        }

        /* Message Rows */
        .message-row-premium {
          display: flex;
          margin-bottom: 0.75rem;
        }

        .user-row {
          justify-content: flex-end;
        }

        .bot-row {
          justify-content: flex-start;
        }

        .message-wrapper-premium {
          max-width: 75%;
          display: flex;
          flex-direction: column;
        }

        /* Message Bubbles */
        .message-bubble-premium {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
          animation: messageSlideIn 0.4s ease-out;
        }

        @keyframes messageSlideIn {
          from {
            opacity: 0;
            transform: translateY(15px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .message-content-premium {
          padding: 1rem 1.375rem;
          border-radius: var(--border-radius-lg);
          font-size: 0.95rem;
          line-height: 1.6;
          word-wrap: break-word;
          white-space: pre-wrap;
          box-shadow: var(--shadow-md);
          position: relative;
          overflow: hidden;
        }

        .user-row .message-content-premium {
          background: var(--primary-gradient);
          color: white;
          border-bottom-right-radius: 8px;
        }

        .user-row .message-content-premium::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
          pointer-events: none;
        }

        .bot-row .message-content-premium {
          background: white;
          color: var(--neutral-800);
          border-bottom-left-radius: 8px;
          border: 2px solid var(--neutral-200);
        }

        .message-meta-premium {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 0 0.75rem;
          margin-top: 0.375rem;
        }

        .message-time-premium {
          font-size: 0.8rem;
          color: var(--neutral-400);
          font-weight: 500;
        }

        .message-status-failed {
          font-size: 0.75rem;
          color: var(--danger-red);
          font-weight: 600;
          background: rgba(250, 112, 154, 0.1);
          padding: 0.25rem 0.5rem;
          border-radius: var(--border-radius-full);
        }

        /* Search Highlight */
        .search-highlight {
          background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
          padding: 0.125rem 0.375rem;
          border-radius: 4px;
          font-weight: 700;
          color: var(--neutral-800);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        /* Premium Footer */
        .premium-footer {
          background: white;
          border-top: 2px solid var(--neutral-200);
          padding: 1.5rem 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          backdrop-filter: var(--backdrop-blur);
        }

        .footer-info-premium {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }

        .message-count-premium {
          display: flex;
          align-items: baseline;
          gap: 0.5rem;
        }

        .count-number {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--primary-purple);
        }

        .count-label {
          font-size: 0.9rem;
          color: var(--neutral-500);
          font-weight: 500;
        }

        .filter-indicator-premium {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          color: var(--primary-purple);
          background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
          padding: 0.5rem 1rem;
          border-radius: var(--border-radius-full);
          font-weight: 600;
          border: 1px solid rgba(102, 126, 234, 0.2);
        }

        .close-modal-btn-premium {
          background: var(--neutral-600);
          border: none;
          border-radius: var(--border-radius);
          padding: 0.875rem 2rem;
          color: white;
          font-weight: 600;
          cursor: pointer;
          transition: var(--transition-normal);
          box-shadow: var(--shadow-sm);
        }

        .close-modal-btn-premium:hover {
          background: var(--neutral-700);
          transform: translateY(-1px);
          box-shadow: var(--shadow-md);
        }

        /* Scrollbar Styling */
        .premium-messages-area::-webkit-scrollbar {
          width: 8px;
        }

        .premium-messages-area::-webkit-scrollbar-track {
          background: var(--neutral-100);
          border-radius: 4px;
        }

        .premium-messages-area::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, var(--neutral-300) 0%, var(--neutral-400) 100%);
          border-radius: 4px;
        }

        .premium-messages-area::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, var(--neutral-400) 0%, var(--neutral-500) 100%);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .premium-conversation-modal .modal-dialog {
            margin: 0.5rem;
            max-width: none;
          }

          .premium-conversation-modal .modal-content {
            height: 95vh;
            border-radius: var(--border-radius-lg);
          }

          .premium-header {
            padding: 1.5rem;
          }

          .premium-filter-bar {
            padding: 1rem;
          }

          .filter-row {
            flex-direction: column;
            align-items: stretch;
            gap: 1rem;
          }

          .search-section-premium {
            min-width: auto;
          }

          .date-filter-toggle {
            min-width: auto;
          }

          .chatbot-avatar-premium {
            width: 50px;
            height: 50px;
          }

          .avatar-inner {
            font-size: 20px;
          }

          .chatbot-title {
            font-size: 1.1rem;
          }

          .session-badge {
            font-size: 0.75rem;
            padding: 0.25rem 0.625rem;
          }

          .action-btn-premium {
            width: 40px;
            height: 40px;
          }

          .message-wrapper-premium {
            max-width: 85%;
          }

          .message-content-premium {
            padding: 0.875rem 1.125rem;
            font-size: 0.9rem;
          }

          .messages-list-premium {
            padding: 1.5rem;
          }

          .premium-footer {
            padding: 1rem 1.5rem;
          }

          .date-inputs {
            flex-direction: column;
            gap: 1rem;
          }

          .date-separator-line {
            width: 2px;
            height: 20px;
            margin: 0;
          }
        }

        @media (max-width: 480px) {
          .premium-conversation-modal .modal-dialog {
            margin: 0.25rem;
          }

          .premium-conversation-modal .modal-content {
            height: 98vh;
            border-radius: var(--border-radius);
          }

          .premium-header {
            padding: 1rem;
          }

          .session-details {
            display: none;
          }

          .message-wrapper-premium {
            max-width: 90%;
          }

          .chatbot-info {
            display: none;
          }

          .header-left {
            gap: 0.75rem;
          }

          .premium-filter-bar {
            padding: 0.75rem;
          }

          .messages-list-premium {
            padding: 1rem;
          }

          .premium-footer {
            padding: 0.75rem 1rem;
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }

          .footer-info-premium {
            justify-content: center;
          }
        }
      `}</style>
    </>
  );
};

export default AIChatbotConversationModal;
